import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface BentoCardProps {
  title: string;
  value: string;
  subtitle: string;
  delay?: number;
  className?: string;
}
function parseValue(value: string) {
  const match = value.match(/^([^\d-]*)(-?[\d,]+)(.*)$/);
  if (!match) return null;
  const prefix = match[1];
  const numStr = match[2].replace(/,/g, '');
  const num = parseInt(numStr, 10);
  if (Number.isNaN(num)) return null;
  const hadCommas = /,/.test(match[2]);
  return {
    prefix,
    num,
    suffix: match[3],
    hadCommas
  };
}
function formatCount(current: number, parsed: NonNullable<ReturnType<typeof parseValue>>) {
  const rounded = Math.round(current);
  const numText = parsed.hadCommas ? rounded.toLocaleString('en-US') : String(rounded);
  return `${parsed.prefix}${numText}${parsed.suffix}`;
}
export function BentoCard({
  title,
  value,
  subtitle,
  delay = 0,
  className = ''
}: BentoCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.3
  });
  const parsed = parseValue(value);
  const [display, setDisplay] = useState<string>(parsed ? `${parsed.prefix}0${parsed.suffix}` : value);
  useEffect(() => {
    if (!inView || !parsed) return;
    const duration = 1500;
    const startDelay = delay * 200;
    let raf = 0;
    let start = 0;
    const tick = (t: number) => {
      if (!start) start = t;
      const elapsed = t - start;
      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setDisplay(formatCount(parsed.num * eased, parsed));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    const timeout = window.setTimeout(() => {
      raf = requestAnimationFrame(tick);
    }, startDelay);
    return () => {
      window.clearTimeout(timeout);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [inView, value, delay]);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.5,
    delay: delay * 0.2,
    ease: [0.16, 1, 0.3, 1]
  }} className={['relative overflow-hidden rounded-[16px] bg-ink', 'flex flex-col justify-between', 'p-6 md:p-7 min-h-[180px] md:min-h-[200px]', className].join(' ')} data-id="element-27">
      {/* Gradient wash overlay */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: 'linear-gradient(135deg, rgba(250,70,22,0.18) 0%, rgba(45,123,255,0.10) 100%)'
    }} data-id="element-28" />
      {/* Bottom accent bar */}
      <div aria-hidden className="absolute left-0 right-0 bottom-0 h-[3px]" style={{
      background: 'linear-gradient(90deg, var(--orange) 0%, var(--orange-deep) 100%)'
    }} data-id="element-29" />

      <div className="relative z-10 font-sans font-medium text-[14px] mb-4" style={{
      color: 'rgba(255,255,255,0.85)'
    }} data-id="element-30">
        {title}
      </div>
      <div className="relative z-10 font-display font-extrabold leading-none tabular-nums" style={{
      color: '#FFFFFF',
      fontSize: 'clamp(32px, 5vw, 56px)',
      letterSpacing: '-.02em'
    }} data-id="element-31">
        {display}
      </div>
      <div className="relative z-10 font-sans font-normal text-[14px] mt-3" style={{
      color: 'rgba(255,255,255,0.75)'
    }} data-id="element-32">
        {subtitle}
      </div>
    </motion.div>;
}